import { ETA_PLACEHOLDER, MAIL_CONTENT_KINDS, TEMPLATE_TYPES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_GetAcceptedOffers,
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetSummary,
    SchemaTemplateResolver_GetMailTemplates,
    SchemaTemplateResolver_GetOfferTemplates,
    SchemaUserResolver_Get,
} from 'cpool-tables/backend-resolvers';
import { getEtaContextForOffers } from 'cpool-utils';
import type { FieldArrayWithId, UseFormRegister } from 'react-hook-form';

import type { InputKind } from '~/features/headerFooterTemplate/types';
import { getEtaTemplatePlaceholders } from '~/utils/mailTemplates/getEtaTemplatePlaceholders';
import {
    FREETEXT_BODY_PLACEHOLDER,
    FREETEXT_OFFER_PLACEHOLDER,
    renderPreviewEtaMailTemplate,
    renderPreviewEtaMailTemplateWithFreetextFields,
} from '~/utils/mailTemplates/renderPreviewEtaMailTemplate';

type Props = {
    lead: SchemaLoanRequestResolver_GetSummary[number] | SchemaLoanRequestResolver_Get;
    template?: SchemaTemplateResolver_GetMailTemplates[number] | SchemaTemplateResolver_GetOfferTemplates[number];
    contentKind: InputKind;
    user: SchemaUserResolver_Get | undefined;
    placeholderText: string;
    offerFields: FieldArrayWithId<
        {
            freetext: {
                offers: {
                    value: string;
                }[];
            };
        },
        'freetext.offers',
        'id'
    >[];
    fields: FieldArrayWithId<
        {
            freetext: {
                body: {
                    value: string;
                }[];
            };
        },
        'freetext.body',
        'id'
    >[];
    register: UseFormRegister<{
        freetext: {
            body: {
                value: string;
            }[];
            offers: {
                value: string;
            }[];
        };
    }>;
    selectedOffers?: SchemaLoanRequestResolver_GetAcceptedOffers;
};

export const getEtaMailContent = ({
    contentKind,
    fields,
    lead,
    offerFields,
    placeholderText,
    register,
    selectedOffers,
    template,
    user,
}: Props) => {
    if (!user || !template) return '';

    const {
        leadPlaceholders,
        trackingPlaceholders,
        userPlaceholders: placeholders,
    } = getEtaTemplatePlaceholders({ lead, template, user });

    const generateOffersList = (offerItemTemplate: string | undefined, isHTML: boolean): string => {
        if (!offerItemTemplate || !selectedOffers || selectedOffers.length === 0) {
            return '';
        }

        const processedOffers = selectedOffers.map((offer) => {
            const etaContext = getEtaContextForOffers({
                applicant1ResidualDebtInsurance: offer?.applicant1ResidualDebtInsurance,
                applicant2ResidualDebtInsurance: offer?.applicant2ResidualDebtInsurance,
                bank: offer?.bank,
                creditAmount: offer?.creditAmount,
                disbursementAmount: offer?.disbursementAmount,
                // NOTE: we cannot return an actual input element here because the eta template renderer would try to stringify it
                // also this needs to be a function to ensure that it works in the backend as well
                freeText: () => FREETEXT_OFFER_PLACEHOLDER,
                interest: offer?.interest,
                nominalInterest: offer?.nominalInterest,
                rate: offer?.rate,
                runtime: offer?.runtime,
            });

            const { renderedText: renderedOffer } = renderPreviewEtaMailTemplate({
                contentKind: isHTML ? MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT : MAIL_CONTENT_KINDS.MAIL_CONTENT,
                etaContext,
                isOfferTemplate: true,
                template: offerItemTemplate,
                templateType: template.type,
            });
            if (Array.isArray(renderedOffer)) {
                return renderedOffer.join('\n');
            }
            return renderedOffer;
        });

        return isHTML ? processedOffers.join('\n') : processedOffers.join('\n\n');
    };

    let offerListPlaceholders = {
        [ETA_PLACEHOLDER.ANGEBOTE_TEXT]: '',
        [ETA_PLACEHOLDER.ANGEBOTE_HTML]: '',
    };

    if (template?.type === TEMPLATE_TYPES.OFFER_MAIL) {
        const offerItemContent = template?.data?.offerItemContent as string | undefined;
        const offerItemHTMLContent = template?.data?.offerItemHTMLContent as string | undefined;

        offerListPlaceholders = {
            [ETA_PLACEHOLDER.ANGEBOTE_TEXT]: generateOffersList(offerItemContent, false),
            [ETA_PLACEHOLDER.ANGEBOTE_HTML]: generateOffersList(offerItemHTMLContent, true),
        };
    }

    let freeTextCount = 0;

    const etaContext = {
        ...placeholders,
        ...trackingPlaceholders,
        ...offerListPlaceholders,
        ...leadPlaceholders,
        freitext: () => {
            // NOTE: we cannot return an actual input element here because the eta template renderer would try to stringify it
            return contentKind === MAIL_CONTENT_KINDS.MAIL_CONTENT
                ? FREETEXT_BODY_PLACEHOLDER
                : `<input type="text" key="${freeTextCount}" class="border border-light-2 rounded-md py-2 my-2 text-sm text-dark-1 w-full" placeholder=${placeholderText} name="freetext.body.${freeTextCount++}.value" />`;
        },
    };

    return renderPreviewEtaMailTemplateWithFreetextFields({
        contentKind,
        etaContext,
        fields,
        freeTextPlaceholderText: placeholderText,
        isOfferTemplate: false,
        offerFields,
        register,
        template: template.data[contentKind],
        templateType: template.type,
    });
};
